import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, LogOut, Music4, Download, Crown, Clock, FileText, Monitor,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { DESKTOP_WIN_URL } from "../lib/constants";

const PLAN_STYLE = {
  "Draft": { color: "#64748B", label: "Draft — Gratuit" },
  "Standard": { color: "#6366F1", label: "Standard" },
  "High / Studio": { color: "#FF5500", label: "High / Studio" },
  "Master Pro": { color: "#10B981", label: "Master Pro — À vie" },
};

export default function Account() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [mixes, setMixes] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: "/account" } }); return; }
    api.get("/mixes").then((r) => setMixes(r.data.mixes)).catch(() => {});
  }, [user, loading]); // eslint-disable-line

  if (!user) return null;
  const plan = PLAN_STYLE[user.plan] || PLAN_STYLE.Draft;

  const dlReport = (m) => {
    const blob = new Blob([m.report || "Rapport indisponible."], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${m.name}.txt`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F8FAFC]">
      <header className="sticky top-0 z-20 bg-[#0B0D12]/95 backdrop-blur border-b border-[#262C3A] px-4 sm:px-6 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/")} className="text-[#64748B] hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></button>
        <Logo className="h-8 w-8" withText textClass="text-lg text-white" />
        <span className="font-mono text-xs uppercase tracking-widest text-[#64748B] hidden sm:inline">· Mon espace</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => navigate("/studio")} data-testid="account-studio-button" className="bg-[#FF5500] hover:bg-[#E04800] rounded-xl px-4 py-2 text-sm font-semibold transition-colors">Ouvrir le Studio</button>
          <button onClick={() => { logout(); navigate("/"); }} className="text-[#64748B] hover:text-white px-2 transition-colors"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {/* profile + plan */}
        <div className="grid md:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="md:col-span-2 bg-[#131722] border border-[#262C3A] rounded-2xl p-6" data-testid="account-profile">
            <p className="font-mono text-xs uppercase text-[#64748B]">Compte</p>
            <h1 className="font-heading text-3xl font-extrabold mt-1">{user.name}</h1>
            <p className="text-[#94A3B8]">{user.email}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-[#131722] border border-[#262C3A] rounded-2xl p-6 flex flex-col justify-between" data-testid="account-plan">
            <div className="flex items-center gap-2"><Crown className="h-5 w-5" style={{ color: plan.color }} /><p className="font-mono text-xs uppercase text-[#64748B]">Plan actif</p></div>
            <div className="font-heading text-2xl font-extrabold mt-2" style={{ color: plan.color }}>{plan.label}</div>
            {user.plan !== "Master Pro" && <button onClick={() => navigate("/#pricing")} className="mt-3 text-sm text-[#FF5500] hover:underline self-start">Améliorer mon plan →</button>}
          </motion.div>
        </div>

        {/* desktop app */}
        <div className="bg-gradient-to-r from-[#131722] to-[#1A1F2C] border border-[#262C3A] rounded-2xl p-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Monitor className="h-6 w-6 text-[#6366F1]" />
            <div>
              <p className="font-semibold">Application Desktop TUBMIX</p>
              <p className="text-sm text-[#94A3B8]">Version Windows installable, 100% hors-ligne.</p>
            </div>
          </div>
          <a href={DESKTOP_WIN_URL} download data-testid="account-desktop-download"
            className="inline-flex items-center gap-2 bg-[#6366F1] hover:bg-[#4f46e5] rounded-xl px-5 py-3 font-semibold transition-colors">
            <Download className="h-4 w-4" /> Télécharger (Windows)
          </a>
        </div>

        {/* history */}
        <div>
          <div className="flex items-center gap-2 mb-4"><Clock className="h-5 w-5 text-[#06B6D4]" /><h2 className="font-heading text-xl font-bold">Historique de mixages (cloud)</h2><span className="text-[#64748B] text-sm">· {mixes.length}/20</span></div>
          <div className="space-y-3" data-testid="account-history">
            {mixes.map((m, i) => (
              <div key={m.id || i} className="bg-[#131722] border border-[#262C3A] rounded-2xl overflow-hidden">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#1A1F2C] transition-colors">
                  <Music4 className="h-5 w-5 text-[#FF5500] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{m.name}</div>
                    <div className="font-mono text-xs text-[#64748B]">{m.quality} · {m.format} · {m.track_count} pistes · {m.bpm} BPM · {m.key}</div>
                  </div>
                  <span className="text-xs text-[#64748B]">{m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : ""}</span>
                </button>
                {open === i && (
                  <div className="border-t border-[#262C3A] p-4 space-y-3">
                    <pre className="text-xs text-[#94A3B8] whitespace-pre-wrap font-mono max-h-52 overflow-auto tx-dark-scroll">{m.report || "Rapport indisponible."}</pre>
                    <button onClick={() => dlReport(m)} className="inline-flex items-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl px-3 py-2 text-sm transition-colors"><FileText className="h-4 w-4" /> Télécharger le rapport</button>
                  </div>
                )}
              </div>
            ))}
            {!mixes.length && (
              <div className="bg-[#131722] border border-[#262C3A] rounded-2xl p-10 text-center text-[#64748B]">
                Aucun mixage sauvegardé. <button onClick={() => navigate("/studio")} className="text-[#FF5500] hover:underline">Ouvre le studio</button> et exporte ton premier mix.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
